/**
 * Page 2 of the booking flow, the parts of the room list with no screen (batch-29, item PK2): the badge of a line, the
 * most people a type takes, the average price of a night, the order and the filter of the list, the nights of the
 * "rate details" window, the day tiles of the stay strip and what a tap on one asks for, and the address a map opens
 * at. Nothing here works out money beyond dividing the server's own stay amount by its nights, and nothing here knows
 * how many rooms a hotel has: a line is Available, Full or Closed — and, since item PK4, "Waiting list" where a full
 * line can be waited for.
 */
import { addDays } from '@/lib/weeks';
import type { PublicNight } from '../public/api';
import type { Row } from './rooms';
import { eventLeave, nightCount, stayNights, stayWindow, type Sold, type StayRules } from './stayDates';

export type RoomSort = 'price_low' | 'price_high' | 'name';
export const SORTS: ReadonlyArray<{ value: RoomSort; label: string }> = [
  { value: 'price_low', label: 'Price: low to high' },
  { value: 'price_high', label: 'Price: high to low' },
  { value: 'name', label: 'Name' },
];
export const sortOf = (raw: unknown): RoomSort => (raw === 'price_high' || raw === 'name' ? raw : 'price_low');

export type RowBadge = 'Available' | 'Full' | 'Waiting list' | 'Closed';
/**
 * The words on a card: a line that can be picked is Available. A full line the guest can wait for — the event's waiting
 * list is on and the server names a type of the line to wait for — reads "Waiting list" in place of "Full" (item PK4).
 * It is still a line that cannot be booked: the filter goes by the line's tag, never by these words. Never a number.
 */
export const badgeOf = (tag: 'Full' | 'Closed' | null, waitable = false): RowBadge => (tag === 'Full' && waitable ? 'Waiting list' : tag ?? 'Available');

/** What each badge means, for the list's info icon. "Waiting list" is named only on an event whose waiting list is on: nowhere else can a card read it. */
export function badgeLegend(waitlistOn: boolean): Array<{ badge: RowBadge; text: string }> {
  const all: Array<{ badge: RowBadge; text: string }> = [
    { badge: 'Available', text: 'You can book this room for your dates.' },
    { badge: 'Full', text: 'Every room of this kind is taken for your dates.' },
    { badge: 'Waiting list', text: 'Taken for your dates. You can ask for a place on the waiting list.' },
    { badge: 'Closed', text: 'Not on sale right now.' },
  ];
  return all.filter((x) => waitlistOn || x.badge !== 'Waiting list');
}

/**
 * The most people a line takes: the type's own number, or the largest of a group's types. Null when any type of the
 * line has no bed numbers set — such a type takes any party, and the card then shows no number at all.
 */
export function mostPeople(row: Pick<Row, 'members'>): number | null {
  if (row.members.length === 0 || row.members.some((m) => m.beds.beds === null)) return null;
  const most = Math.max(...row.members.map((m) => m.beds.maxPeople));
  return Number.isFinite(most) && most >= 1 ? most : null;
}

/** The average price of a night: the server's stay amount over its nights, to the cent. Null when the stay has no amount (a night with no price) or no nights. */
export function averageNight(stayCents: number | null | undefined, nights: number): number | null {
  if (typeof stayCents !== 'number' || !Number.isFinite(stayCents) || !Number.isInteger(nights) || nights < 1) return null;
  return Math.round(stayCents / nights);
}

/** One line of the list with what the order and the filter go by. */
export interface Listed<T> { item: T; name: string; cents: number | null; heading: string | null; tag: 'Full' | 'Closed' | null }

/**
 * The list in the order asked for. Lines with no price come last, in the organizer's order; equal lines keep the
 * organizer's order. Where the hotel lists its types under group headings, the order is inside each heading and the
 * headings stay where the organizer put them.
 */
export function sortListed<T>(list: ReadonlyArray<Listed<T>>, sort: RoomSort): Array<Listed<T>> {
  const blocks: Array<Array<{ x: Listed<T>; i: number }>> = [];
  list.forEach((x, i) => {
    const last = blocks[blocks.length - 1];
    if (last && last[0]!.x.heading === x.heading) last.push({ x, i });
    else blocks.push([{ x, i }]);
  });
  const by = (a: { x: Listed<T>; i: number }, b: { x: Listed<T>; i: number }): number => {
    if (sort === 'name') return a.x.name.localeCompare(b.x.name, 'en', { numeric: true, sensitivity: 'base' }) || a.i - b.i;
    if (a.x.cents === null || b.x.cents === null) return a.x.cents === b.x.cents ? a.i - b.i : a.x.cents === null ? 1 : -1;
    return (sort === 'price_high' ? b.x.cents - a.x.cents : a.x.cents - b.x.cents) || a.i - b.i;
  };
  return blocks.flatMap((block) => [...block].sort(by).map((e) => e.x));
}

/** "Available only": the lines that can be picked. Off, every line. */
export function filterListed<T>(list: ReadonlyArray<Listed<T>>, availableOnly: boolean): Array<Listed<T>> {
  return availableOnly ? list.filter((x) => x.tag === null) : [...list];
}

/** One night of the "rate details" window: its price, or null when the night has none or is not on sale. */
export interface NightRate { night: string; cents: number | null }
/** The nights of a stay with the price the server sent for each. Empty when rooms are not charged or the type carries no nights. */
export function nightRates(nights: Record<string, PublicNight> | undefined, days: readonly string[], charged: boolean): NightRate[] {
  if (!charged || !nights) return [];
  return days.map((night) => {
    const n = nights[night];
    return { night, cents: n && n.sold !== false && typeof n.p === 'number' ? n.p : null };
  });
}

export type TileState = 'selected' | 'available' | 'unavailable';
/** One day tile of the stay strip: a night. `event` — a night of the event itself; `cents` — the night's price, when the strip is of one priced type. */
export interface StripTile { day: string; state: TileState; event: boolean; cents: number | null }

/** Every night a stay may hold, first to last, as tiles: the stay's own nights selected, a night not on sale unavailable. */
export function stripTiles(rules: StayRules, stay: { arrive: string; leave: string }, sold: Sold, priceOf?: (day: string) => number | null): StripTile[] {
  const w = stayWindow(rules);
  const leave = eventLeave(rules);
  return stayNights(w.first, w.last).map((day) => {
    const on = sold(day);
    const cents = on && priceOf ? priceOf(day) : null;
    return {
      day, event: day >= rules.start && day < leave, cents,
      state: !on ? 'unavailable' : day >= stay.arrive && day < stay.leave ? 'selected' : 'available',
    };
  });
}

/**
 * What a tap on a night asks for: a night before or after the stay stretches the stay to it; the stay's first or last
 * night comes off; a night in the middle, or the only night, asks for nothing. Whether the event allows the stay asked
 * for is the date rules' own answer (stayDates.ts) — nothing is decided here.
 */
export function tapNight(stay: { arrive: string; leave: string }, day: string): { arrive: string; leave: string } | null {
  const n = nightCount(stay.arrive, stay.leave);
  if (!Number.isFinite(n) || n < 1) return { arrive: day, leave: addDays(day, 1) };
  if (day < stay.arrive) return { arrive: day, leave: stay.leave };
  if (day >= stay.leave) return { arrive: stay.arrive, leave: addDays(day, 1) };
  if (n === 1) return null;
  if (day === stay.arrive) return { arrive: addDays(day, 1), leave: stay.leave };
  if (day === addDays(stay.leave, -1)) return { arrive: stay.arrive, leave: day };
  return null;
}

/** How many tiles the strip shows at once: two weeks. */
export const STRIP_PAGE = 14;
/** The page of the strip a day is on. */
export const stripPageOf = (tiles: readonly StripTile[], day: string): number => {
  const at = tiles.findIndex((t) => t.day === day);
  return at < 0 ? 0 : Math.floor(at / STRIP_PAGE);
};

/** What a screen reader is told a tile is: its state, and whether it is a night of the event. */
export function tileWords(t: StripTile): string {
  const state = t.state === 'selected' ? 'in your stay' : t.state === 'available' ? 'available' : 'not available';
  return t.event ? `${state}, a night of the event` : state;
}

/** Where "Show on map" opens: a map search for the hotel's address. Null with no address. */
export function mapUrl(name: string | null | undefined, address: string | null | undefined): string | null {
  const a = (address ?? '').trim();
  if (!a) return null;
  const q = [(name ?? '').trim(), a].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}
